import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useLocation } from 'react-router-dom';
import { AlertCircle, ChevronDown, ClipboardCheck, Sun, Tractor } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../services/AuthContext';
import NeedsAttention from '../components/NeedsAttention';
import PlanBuyCard from '../components/PlanBuyCard';
import { FieldDay } from './FieldHome';
import { MyDayBlocks } from './MyDay';
import { YardCheck } from './ManagerCheck';
import { iso } from './BidderHome';

/**
 * Today — the one morning page.
 *
 * Owner, Nov 2 2026: "combine Field and My Day, put Needs Attention on Field
 * and My Day, manager check added to My Day and Field." One page he scrolls
 * on the phone, top to bottom in the order he works it:
 *
 *   Needs Attention — what needs a hand (components/NeedsAttention, whole)
 *   Field           — his stops, quote visits and the lawns to inspect (FieldDay)
 *   My Day          — texts waiting, crew, reminders, checklists, to-dos (MyDayBlocks)
 *   Manager check   — the yard walk, one item at a time (YardCheck)
 *
 * Each block folds, and the phone remembers which ones he folded
 * (localStorage). An empty block folds itself to one quiet line. The old
 * pages (/field, /my-day, /attention, /check) render this page scrolled to
 * their block, so every bookmark and the hat switcher still land him on his
 * day; /today#field and the like do the same.
 *
 * Nothing inside a block changed: the blocks are the pages' own components,
 * and their pages still render them whole.
 */

type BlockKey = 'attention' | 'field' | 'my-day' | 'check';
const BLOCKS: BlockKey[] = ['attention', 'field', 'my-day', 'check'];
const STORE = 'today.open.';

function readOpen(key: BlockKey): boolean {
  try { return localStorage.getItem(STORE + key) !== 'closed'; } catch { return true; }
}
function writeOpen(key: BlockKey, open: boolean) {
  try { localStorage.setItem(STORE + key, open ? 'open' : 'closed'); } catch { /* a private window — the page still works */ }
}

function Block({ id, title, icon: Icon, summary, count, empty, quiet, open, onToggle, more, children }: {
  id: BlockKey; title: string; icon: React.ElementType;
  summary?: React.ReactNode;        // the one line under the title while the block is shut (and open)
  count?: number | null;            // the badge
  empty?: boolean;                  // nothing in it: folds itself to the quiet line
  quiet: string;                    // the one line an empty block shows
  open: boolean; onToggle: () => void;
  more?: { to: string; label: string };
  children: React.ReactNode;
}) {
  const shut = !open;
  return (
    <section id={`today-${id}`} className="scroll-mt-20">
      <div className={`flex items-center gap-2 ${shut ? '' : 'mb-3'}`}>
        <button type="button" onClick={onToggle} aria-expanded={!shut}
          className="flex-1 min-w-0 flex items-center gap-2 text-left py-1">
          <Icon className={`h-4 w-4 flex-shrink-0 ${empty ? 'text-gray-300' : 'text-gray-600'}`} />
          <span className="flex-1 min-w-0">
            <span className="flex items-center gap-2">
              <span className="text-base font-black uppercase tracking-tight text-gray-900">{title}</span>
              {count != null && count > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">{count}</span>
              )}
            </span>
            <span className={`block text-xs truncate ${empty ? 'text-gray-400' : 'text-gray-500'}`}>{empty ? quiet : summary}</span>
          </span>
          <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${shut ? '-rotate-90' : ''}`} />
        </button>
        {more && !shut && <Link to={more.to} className="text-xs font-semibold text-brand-700 flex-shrink-0 px-1">{more.label}</Link>}
      </div>
      {/* Shut blocks stay mounted (hidden), so Field keeps its day, its plan and its offline queue, and nothing refetches on a fold. */}
      <div className={shut ? 'hidden' : ''}>{children}</div>
    </section>
  );
}

const offTheDay = (st: any) => ['canceled', 'cancelled', 'skipped'].includes(String(st || ''));

export default function Today({ focus }: { focus?: BlockKey }) {
  const { user } = useAuth();
  const { hash } = useLocation();
  const role = user?.role;
  // The admin side only. The admin shell (App.tsx RequireAdmin) already keeps every other hat out; this is the
  // belt to its braces, block by block, the way the old pages were gated: a crew login never sees an office block.
  const office = role === 'admin' || role === 'viewer';
  const canSee: Record<BlockKey, boolean> = {
    attention: office,
    field: office,
    'my-day': office,
    check: office || role === 'manager',
  };

  // Which block the URL points at: #field (the jump links, /today#field) first, else the old page's own
  // block (/field, /my-day, /attention, /check). A hash that is not a block (/attention#service:12, a row
  // NeedsAttention lights up) leaves the page's block in charge.
  const hashed = hash.slice(1) as BlockKey;
  const wanted: BlockKey | null = BLOCKS.includes(hashed) ? hashed : (focus || null);

  const [open, setOpen] = useState<Record<BlockKey, boolean>>(() => ({
    attention: readOpen('attention'), field: readOpen('field'), 'my-day': readOpen('my-day'), check: readOpen('check'),
  }));
  // An empty block folds itself; a tap opens it for this visit only (its stored choice is not touched).
  const [peek, setPeek] = useState<Partial<Record<BlockKey, boolean>>>({});
  const toggle = (key: BlockKey, empty: boolean) => {
    if (empty) { setPeek(p => ({ ...p, [key]: !p[key] })); return; }
    setOpen(o => { const next = !o[key]; writeOpen(key, next); return { ...o, [key]: next }; });
  };

  // Landing on a block: it opens and the page scrolls to it — once the blocks have drawn, and again
  // a moment later when their lists have come in and moved everything down.
  useEffect(() => {
    if (!wanted) return;
    setOpen(o => (o[wanted] ? o : (writeOpen(wanted, true), { ...o, [wanted]: true })));
    setPeek(p => ({ ...p, [wanted]: true }));
    const go = () => document.getElementById(`today-${wanted}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    const t1 = setTimeout(go, 60), t2 = setTimeout(go, 700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [wanted]);

  // ── The headers' numbers: the same queries the blocks make, by the same keys, so nothing is fetched twice ──
  const today = iso(new Date());
  const cm = (user as any)?.crewMemberId || null;
  const { data: attention } = useQuery<any>('attention', () => api.get('/attention').then(r => r.data), { enabled: canSee.attention });
  // The same question FieldDay asks, by the same key, so the header's numbers
  // and the block's list are one fetch: the whole day for the office, each stop
  // stamped `mine` (server/utils/dayScope). The header still counts HIS stops;
  // the crew's routes are counted separately so an empty list of his own does
  // not read as an empty day.
  const stopsQ = useQuery(['field-stops', today, cm, office ? 'all' : 'mine'],
    () => api.get('/jobs', { params: { date: today, ...(cm ? { crew_member_id: cm } : {}), ...(office ? { all_crews: 1 } : {}) } })
      .then(r => r.data as { jobs: any[]; day_groups?: any[] }),
    { enabled: canSee.field && (!!cm || office), retry: false });
  const visitsQ = useQuery(['bid-visits', today, today],
    () => api.get('/quotes/visits', { params: { from: today, to: today } }).then(r => r.data.visits as any[]),
    { enabled: canSee.field, retry: false });
  const { data: day } = useQuery('my-day', () => api.get('/day/owner').then(r => r.data), { enabled: canSee['my-day'] || canSee.check });

  const attentionTotal: number | null = attention?.counts?.total ?? null;
  const doFirst: number = (attention?.groups || []).find((g: any) => g.key === 'now')?.items?.length ?? 0;
  const attentionEmpty = attention != null && attentionTotal === 0;

  const mows = (stopsQ.data?.jobs || []).filter(j => (j.service_type || 'mowing') !== 'estimate' && !offTheDay(j.status) && j.mine !== false);
  // What the crew has on today — the groups the Field block lists under "The rest of the day".
  const elsewhere = (stopsQ.data?.day_groups || []).reduce((n: number, g: any) => n + (Number(g.total) || 0), 0);
  const visits = (visitsQ.data || []).filter(v => !offTheDay(v.status));
  const stops = mows.length + visits.length;
  const stopsOpen = mows.filter(j => j.status !== 'complete').length + visits.filter(v => v.status !== 'complete').length;
  const fieldLoaded = ((!cm && !office) || stopsQ.data != null || stopsQ.isError) && (visitsQ.data != null || visitsQ.isError);
  const fieldEmpty = fieldLoaded && stops === 0 && elsewhere === 0;
  const crewWords = elsewhere ? `${elsewhere} on the crew's routes` : '';
  const fieldWords = !fieldLoaded ? 'Loading…'
    : [stops === 0 ? 'Nothing of your own today' : stopsOpen === 0 ? 'All done for today'
        : `${stopsOpen} to do${visits.length ? ` · ${visits.length} quote visit${visits.length === 1 ? '' : 's'}` : ''}`,
       crewWords].filter(Boolean).join(' · ');

  const due = (day?.reminders?.due || []).length;
  const todos = (day?.todos || []).filter((t: any) => t.status !== 'complete').length + (day?.todos_open || []).length;
  const crew = day?.crew || {};
  const myDayWords = !day ? 'Loading…'
    : [due ? `${due} to reach out to` : null, todos ? `${todos} to-do${todos === 1 ? '' : 's'}` : null,
       (crew.routes || []).length ? `crew ${crew.done ?? 0}/${crew.total ?? 0} done` : 'no route running']
      .filter(Boolean).join(' · ');

  const check = day?.check;
  const checkDone = check?.status === 'complete';
  const checkWords = !day ? 'Loading…'
    : !check ? 'Morning check not started'
    : checkDone ? `Release: ${check.summary?.release}${check.holds?.length ? ` · ${check.holds.length} on hold` : ' · clear'}`
    : check.progress.answered === 0 ? 'Morning check not started'
    : `${check.progress.answered}/${check.progress.total} · next: ${check.next?.label || ''}`;

  const dayWords = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const shown = BLOCKS.filter(k => canSee[k]);
  const isOpen = (k: BlockKey, empty: boolean) => (empty ? !!peek[k] : open[k]);

  if (!shown.length) {
    return (
      <div className="max-w-2xl mx-auto py-10 text-center">
        <p className="text-sm text-gray-600">Nothing here for this login.</p>
        <Link to="/crew/dashboard" className="text-sm font-semibold text-brand-700">Back to my day</Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8 pb-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Today</h1>
        <p className="text-xs text-gray-500">{dayWords}</p>
        {/* One tap to a block — the page is three screens tall on a phone. */}
        <nav className="mt-2 flex flex-wrap gap-1.5 text-xs font-semibold">
          {shown.map(k => (
            <a key={k} href={`#${k}`} className="px-2.5 py-1 rounded-full border border-gray-200 bg-white text-gray-700">
              {k === 'attention' ? `Needs a hand${attentionTotal ? ` · ${attentionTotal}` : ''}` : k === 'field' ? 'Field' : k === 'my-day' ? 'My Day' : 'Manager check'}
            </a>
          ))}
        </nav>
      </div>

      {canSee.attention && (
        <Block id="attention" title="Needs Attention" icon={AlertCircle}
          count={attentionTotal} empty={attentionEmpty} quiet="Nothing needs a hand"
          summary={attention == null ? 'Loading…' : doFirst ? `${doFirst} to do first` : `${attentionTotal} on the list`}
          open={isOpen('attention', attentionEmpty)} onToggle={() => toggle('attention', attentionEmpty)}
          more={{ to: '/attention', label: 'Full page' }}>
          <div className="space-y-4">
            <PlanBuyCard />
            <NeedsAttention />
          </div>
        </Block>
      )}

      {canSee.field && (
        <Block id="field" title="Field" icon={Tractor}
          count={fieldLoaded ? stops : null} empty={fieldEmpty} quiet="Nothing on your list today"
          summary={fieldWords}
          open={isOpen('field', fieldEmpty)} onToggle={() => toggle('field', fieldEmpty)}>
          <FieldDay embedded />
        </Block>
      )}

      {canSee['my-day'] && (
        <Block id="my-day" title="My Day" icon={Sun}
          empty={false} quiet="" summary={myDayWords}
          open={isOpen('my-day', false)} onToggle={() => toggle('my-day', false)}>
          <MyDayBlocks embedded />
        </Block>
      )}

      {canSee.check && (
        <Block id="check" title="Manager check" icon={ClipboardCheck}
          empty={!!checkDone} quiet={checkWords} summary={checkWords}
          open={isOpen('check', !!checkDone)} onToggle={() => toggle('check', !!checkDone)}
          more={{ to: '/check', label: 'Clipboard' }}>
          <YardCheck />
        </Block>
      )}
    </div>
  );
}
